import * as React from "react";
import { Link } from "@tanstack/react-router";
import * as m from "~/paraglide/messages";
import { ThemeToggle } from "~/lib/theme";
import { RssTicker } from "~/components/RssTicker";
import { LanguageSwitcher } from "~/components/LanguageSwitcher";
import { icons } from "./SidebarIcons";
import { AlchemaLogo } from "~/components/AlchemaLogo";
import { LocalizedBlogLink } from "~/components/blog/BlogLink";

/* ── Landing page nav (unauthenticated) ── */

export function LandingNav({ session }: { session?: { user?: { name?: string | null } | null } | null }) {
  const [logoError, setLogoError] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 flex justify-center px-4 pt-4">
        <nav className="n8n-header animate-fade-up" aria-label="Main navigation">
          <Link to="/" className="flex min-h-[44px] min-w-[44px] items-center shrink-0 gap-2" aria-current="page" aria-label="Alchema home">
            <AlchemaLogo darkBg style={{ fontSize: "1.5rem" }} />
          </Link>

          {/* Center: RSS ticker on desktop */}
          <div className="hidden lg:flex flex-1 justify-center px-4">
            <RssTicker />
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            {/* Desktop nav items — hidden on mobile, shown sm+ */}
            <div className="hidden sm:flex items-center gap-1 sm:gap-2">
              <LocalizedBlogLink href="/blog" className="n8n-header-btn-ghost hidden md:inline-flex">
                {m.nav_blog()}
              </LocalizedBlogLink>
              <Link to="/pricing" className="n8n-header-btn-ghost hidden md:inline-flex">
                {m.nav_pricing()}
              </Link>
              <div className="hidden md:block [&_button]:!text-white/80 [&_button:hover]:!text-white"><LanguageSwitcher /></div>
              <div className="[&_button]:!text-white/80 [&_button:hover]:!text-white"><ThemeToggle /></div>
              {session?.user ? (
                <Link to="/dashboard" className="n8n-header-btn-primary">
                  {m.nav_dashboard()}
                </Link>
              ) : (
                <>
                  <Link to="/auth/login" className="n8n-header-btn-ghost">
                    {m.nav_sign_in()}
                  </Link>
                  <Link to="/auth/signup" className="n8n-header-btn-primary">
                    {m.common_get_started()}
                  </Link>
                </>
              )}
            </div>
            {/* Mobile hamburger — visible below sm: logo + hamburger only */}
            <button
              className="flex h-11 w-11 items-center justify-center rounded-lg sm:hidden"
              style={{ color: "rgba(255,255,255,0.8)" }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              <span className="h-5 w-5">{mobileMenuOpen ? icons.close : icons.menu}</span>
            </button>
          </div>
        </nav>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div
            className="absolute top-full left-4 right-4 mt-2 flex flex-col gap-1 rounded-2xl p-3 sm:hidden"
            style={{
              background: "rgba(12, 12, 16, 0.85)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            {session?.user ? (
              <Link
                to="/dashboard"
                className="flex items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold text-white transition-colors"
                style={{ background: "var(--accent)" }}
                onClick={() => setMobileMenuOpen(false)}
              >
                {m.nav_dashboard()}
              </Link>
            ) : (
              <>
                <Link
                  to="/auth/signup"
                  className="flex items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold text-white transition-colors"
                  style={{ background: "var(--accent)" }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {m.common_get_started()}
                </Link>
                <Link
                  to="/auth/login"
                  className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium text-white/80 transition-colors hover:bg-white/10"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {m.nav_sign_in()}
                </Link>
              </>
            )}
            <LocalizedBlogLink
              href="/blog"
              className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium text-white/80 transition-colors hover:bg-white/10"
              onClick={() => setMobileMenuOpen(false)}
            >
              {m.nav_blog()}
            </LocalizedBlogLink>
            <Link
              to="/pricing"
              className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium text-white/80 transition-colors hover:bg-white/10"
              onClick={() => setMobileMenuOpen(false)}
            >
              {m.nav_pricing()}
            </Link>
            <div className="flex items-center gap-4 px-4 py-2">
              <div className="[&_button]:!text-white/80 [&_button:hover]:!text-white"><ThemeToggle /></div>
              <div className="[&_button]:!text-white/80 [&_button:hover]:!text-white"><LanguageSwitcher /></div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
